import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClickedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
} from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { uniq } from 'lodash';
import { combineLatest, EMPTY, firstValueFrom, map, switchMap } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

import { BalanceSheetEntitySettingsKeys } from '@widgets/new-manage-balance-sheets-accounts-modal/components/balance-sheet-account-grid/grid-config/grid-config-context';
import { injectManageBalanceSheetsAccountsModal } from '@widgets/new-manage-balance-sheets-accounts-modal/new-manage-balance-sheets-accounts-component-modal.component';

import { getTrialAttributes } from '@features/budget-attributes/services/budget-attributes.service';
import { ExchangeLinkComponent } from '@features/exchange-link/exchange-link.component';

import { ButtonComponent } from '@shared/components/button/button.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { TablePanelComponent } from '@shared/components/table-panel/table-panel.components';
import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { ToggleComponent } from '@shared/components/toggle/toggle.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { EntityType, EventType, GqlService } from '@shared/services/gql.service';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { QuarterCloseChecklistPeriodCloseService } from '@shared/services/quarter-close-checklist-period-close.service';
import { injectPermissions } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { GlobalStore } from '@shared/store/global/global.store';
import { MainQuery } from '@shared/store/main/main.query';
import { CurrencyToggle } from '@shared/types/components.type';
import { decimalAddReduce, evenRounding } from '@shared/utils';
import { signalRequestWithRefresh } from '@shared/utils/signal-request-with-refresh';
import { ExportType } from '@shared/utils/utils';

import { HowItWorksModalComponent } from './how-it-works-modal/how-it-works-modal.component';
import { getJournalEntriesGridData, journalEntriesGridOptions } from './je-grid-config';
import { JournalEntriesGridRow, JournalEntriesGridTotalRow } from './journal-entries.model';
import { PeriodCloseComponent } from '../../period-close.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TableSkeletonComponent,
    NgSelectComponent,
    FormsModule,
    NgOptionComponent,
    ButtonComponent,
    AgGridAngular,
    StickyGridDirective,
    CheckboxComponent,
    TablePanelComponent,
    TooltipDirective,
    ToggleComponent,
    TooltipDirective,
    ExchangeLinkComponent,
  ],
  selector: 'aux-journal-entries',
  styleUrl: 'journal-entries.component.scss',
  templateUrl: 'journal-entries.component.html',
})
export class JournalEntriesComponent {
  eventService = inject(EventService);
  globalStore = inject(GlobalStore);
  gqlService = inject(GqlService);
  mainQuery = inject(MainQuery);
  overlayService = inject(OverlayService);
  periodCloseComponent = inject(PeriodCloseComponent);
  periodCloseService = inject(QuarterCloseChecklistPeriodCloseService);
  route = inject(ActivatedRoute);
  router = inject(Router);

  flags = injectFlags({
    isApManualClearingVisible: 'journal_entry_ap_manual_clearing',
    isCurrencyToggleVisible: 'journal_entries_currency_toggle',
    isHowItWorksVisible: 'journal_entries_how_it_works_link',
  });
  manageBalanceSheetsAccountsModal = injectManageBalanceSheetsAccountsModal();
  permissions = injectPermissions({
    manageBalanceSheetsAccounts: 'PERMISSION_UPDATE_BALANCE_SHEET_ACCOUNT',
  });

  gridAPI = signal<GridApi<JournalEntriesGridRow> | null>(null);
  gridDataLoading = signal(false);

  // todo use injectLocalStorage from ng-extension
  hideRowsCheckBoxLSKey = `hide_empty_rows_${this.mainQuery.getValue().trialKey}`;

  hideRowsCheckBox = signal(JSON.parse(localStorage.getItem(this.hideRowsCheckBoxLSKey) || 'true'));
  selectedMonth = signal('');
  rawGridData = signalRequestWithRefresh(
    combineLatest([
      toObservable(this.flags),
      toObservable(this.selectedMonth),
      toObservable(this.globalStore.primaryAttribute),
      toObservable(this.globalStore.secondaryAttribute),
    ]).pipe(
      tap(() => this.gridDataLoading.set(true)),
      debounceTime(500),
      switchMap(([flags, fromDate, primary, secondary]) => {
        const toDate = dayjs(fromDate).add(1, 'month').format('YYYY-MM-DD');

        if (!fromDate || toDate === 'Invalid Date') {
          return EMPTY;
        }

        return this.gqlService
          .listJournalEntries$({
            ap_manual_clearing: flags.isApManualClearingVisible,
            attribute_group: primary.attribute_name
              ? JSON.stringify(
                  [
                    {
                      attribute_name: primary.attribute_name,
                    },
                    secondary.attribute_name
                      ? {
                          attribute_name: secondary.attribute_name,
                        }
                      : null,
                  ].filter(Boolean)
                )
              : undefined,
            from_date: fromDate,
            to_date: toDate,
          })
          .pipe(
            map((result) => {
              this.gridDataLoading.set(false);

              if (result.success && result.data) {
                return result.data;
              }

              return [];
            })
          );
      })
    ),
    []
  );

  currencyToggleLSKey = computed(() => {
    return `journal_entries_currency_toggle_${this.mainQuery.selectedTrial()?.id || ''}`;
  });

  currencyToggle = linkedSignal<`${CurrencyToggle}`>(() => {
    const storedValue =
      localStorage.getItem(this.currencyToggleLSKey()) || CurrencyToggle.CONTRACTED;

    if (this.showCurrencyToggle()) {
      return storedValue as `${CurrencyToggle}`;
    }

    return 'contracted';
  });

  gridData = computed(() => {
    const rawGridData = this.rawGridData.value();

    return getJournalEntriesGridData(rawGridData, this.currencyToggle());
  });

  getPinnedBottomData = (gridData: JournalEntriesGridRow[]) => {
    const currencies = new Set(gridData.map((row) => row.vendor_currency));
    const currencyToggle = this.currencyToggle();

    if (currencies.size > 1 && currencyToggle === CurrencyToggle.CONTRACTED) {
      return [];
    }

    return [
      {
        ...this.generatePinnedBottomData(gridData),
        vendor_currency: currencies.values().next().value,
      },
    ];
  };

  redirectFn = (params: CellClickedEvent | ICellRendererParams): void => {
    const vendorId = params.data ? params.data?.vendor_id : params.node.key;

    void this.router.navigate(
      [`/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.CLOSE_SUMMARY}`],
      {
        queryParams: {
          jeSelectedMonth: this.selectedMonth(),
          jeVendorId: vendorId,
        },
      }
    );
  };

  gridOptions = computed(() => {
    const attributeConfig = {
      attributes: getTrialAttributes(
        this.globalStore.attributes(),
        this.globalStore.primaryAttribute().attribute_name,
        this.globalStore.secondaryAttribute().attribute_name
      ),
      primaryAttribute: this.globalStore.primaryAttribute(),
      secondaryAttribute: this.globalStore.secondaryAttribute(),
    };
    const gridData = this.gridData();
    const currencyToggle = this.currencyToggle();

    return untracked(() => {
      return <GridOptions<JournalEntriesGridRow>>{
        ...journalEntriesGridOptions({
          attributeConfig: {
            ...attributeConfig,
            gridData,
          },
          currencyToggle,
          redirectFn: this.redirectFn,
        }),
        doesExternalFilterPass: (node: IRowNode) => {
          return (
            !this.hideRowsCheckBox() ||
            evenRounding(node.data.debit_amount, 2) !== 0 ||
            evenRounding(node.data.credit_amount, 2) !== 0
          );
        },
        pinnedBottomRowData: this.getPinnedBottomData(gridData),
      };
    });
  });

  trialAttributesLoading = this.globalStore.getLoading('TrialAttributes');

  loading = computed(() => {
    return this.gridDataLoading() || this.trialAttributesLoading();
  });
  manageBalanceSheetsAccountsPermissionTooltip = computed(() => {
    const permission = this.permissions().manageBalanceSheetsAccounts;

    if (!permission) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    return '';
  });

  selectedQuarterMonths = this.periodCloseComponent.selectedQuarterMonths;

  monthOptions = computed(() => {
    const currentMonth = dayjs(this.mainQuery.currentOpenMonth());
    const selectedQuarterMonths = this.selectedQuarterMonths();

    return selectedQuarterMonths.map((month) => {
      const date = dayjs(month);
      let label = date.format('MMMM YYYY');

      if (date.isBefore(currentMonth)) {
        label += ' (Closed)';
      } else if (date.isSame(currentMonth)) {
        label += ' (Open)';
      }

      return {
        label,
        value: month,
      };
    });
  });
  monthTitle = computed(() => {
    const selectedMonth = this.selectedMonth();

    if (!selectedMonth) {
      return '';
    }

    return dayjs(selectedMonth).format('MMMM YYYY');
  });
  showCurrencyToggle = computed(() => {
    return (
      !this.periodCloseComponent.isVendorCurrenciesUSD() && this.flags().isCurrencyToggleVisible
    );
  });

  _syncCurrencyToggle = effect(() => {
    localStorage.setItem(this.currencyToggleLSKey(), this.currencyToggle());
  });
  _syncHideRowsCheckBox = effect(() => {
    localStorage.setItem(this.hideRowsCheckBoxLSKey, this.hideRowsCheckBox());
    this.gridAPI()?.onFilterChanged();
    this.gridAPI()?.redrawRows();
  });
  _syncPersistedMonth = effect(() => {
    const selectedMonth = this.selectedMonth();

    if (selectedMonth) {
      this.periodCloseService.persistedQuarterMonth = selectedMonth;
      this.periodCloseService.selectedQuarterMonth = selectedMonth;
      this.periodCloseService.selectedQuarterMonthChanged$.next(null);
    }
  });
  _syncSelectedMonth = effect(() => {
    const selectedQuarterMonths = this.selectedQuarterMonths();
    const currentMonth = untracked(() => this.mainQuery.currentOpenMonth());
    const oldSelectedMonth = untracked(() => this.selectedMonth());
    const routeMonth = this.route.snapshot.queryParams.month;
    // todo refactor persistedMonth
    const persistedMonth = this.periodCloseService.persistedQuarterMonth;

    const isMonthInList = (month: string) => selectedQuarterMonths.includes(month);

    // If the selected month is already in the list, do nothing
    if (isMonthInList(oldSelectedMonth)) {
      return;
      // If the route has a month, set it and make sure it also sets the quarter
    }

    if (routeMonth) {
      this.selectedMonth.set(routeMonth);

      void this.router.navigate([], {
        queryParams: {
          month: null,
        },
        queryParamsHandling: 'merge',
      });
      const month = dayjs(routeMonth);
      const quarter = `Q${month.quarter()}-${month.year()}`;

      // this will cause the current effect to run again! That's why we use the return above
      this.periodCloseComponent.selectedQuarter.setValue(quarter);

      return;
    }

    if (isMonthInList(persistedMonth)) {
      this.selectedMonth.set(persistedMonth);

      return;
    }

    if (isMonthInList(currentMonth)) {
      this.selectedMonth.set(currentMonth);

      return;
    }

    this.selectedMonth.set(selectedQuarterMonths[0]);
  });
  currencyToggle$ = toObservable(this.currencyToggle);

  constructor() {
    this.globalStore.refreshRequest('TrialAttributes');
  }

  generatePinnedBottomData(gridData: JournalEntriesGridRow[]): JournalEntriesGridTotalRow {
    return decimalAddReduce(gridData, ['debit_amount', 'credit_amount']);
  }

  onCurrencyToggleChange(value: `${CurrencyToggle}`) {
    this.currencyToggle.set(value);

    // this is a hack to get the grid to update the pinned bottom row data
    setTimeout(() => {
      this.gridAPI()?.updateGridOptions({
        pinnedBottomRowData: this.getPinnedBottomData(this.gridData()),
      });
    }, 0);
  }

  async onExport() {
    const trialShortName = this.mainQuery.getSelectedTrial()!.short_name || '';
    const trialId = this.mainQuery.getSelectedTrial()!.id;
    const month = dayjs(this.selectedMonth());
    const monthYear = month.format('MMMYYYY');
    const now = dayjs().format('YYYY.MM.DD-HHmmss');

    const result = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: trialId,
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_entity_id: trialId,
          export_type: ExportType.JOURNAL_ENTRIES,
          filename: `${trialShortName}_auxilius_${monthYear}_Journal_Entries_${now}`,
          json_properties: {
            is_hide_rows_checkbox_checked: this.hideRowsCheckBox(),
            is_trial_currency: this.currencyToggle() === CurrencyToggle.PRIMARY,
            month: month.format('MM/DD/YY'),
          },
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (result.success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(result.errors);
    }
  }

  onGridReady({ api }: GridReadyEvent): void {
    this.gridAPI.set(api);
  }

  openHowItWorksModal(): void {
    this.overlayService.openPopup({
      content: HowItWorksModalComponent,
      settings: {
        header: 'Journal Entry Rules & Logic',
        primaryButton: {
          label: 'OK',
        },
        secondaryButton: {
          hidden: true,
        },
      },
    });
  }

  async openManageBalanceSheetsAccountsModal(): Promise<void> {
    const rowData = this.gridData();
    const relatedRowEntryTypes = Object.values(BalanceSheetEntitySettingsKeys) as string[];
    const relatedRowData = rowData.filter((row) => relatedRowEntryTypes.includes(row.entry_type));
    const programOptions = uniq(
      relatedRowData.map((row) => row.program).filter(Boolean)
    ) as string[];
    const projectOptions = uniq(
      relatedRowData.map((row) => row.project).filter(Boolean)
    ) as string[];

    const modalRef = this.manageBalanceSheetsAccountsModal({
      programOptions,
      projectOptions,
    });

    const { data } = await firstValueFrom(modalRef.afterClosed$);

    if (data) {
      this.globalStore.refreshRequest('TrialAttributes');
      this.rawGridData.refresh();
    }
  }
}
